import { useLazyQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { GET_COMPONENTS_BY_VENDOR } from '@api/queries/components';
import { GET_ALL_COMPONENT_VENDORS_BY_NAME } from '@api/queries/vendor';
import { type SimpleComponent } from '@components/NewComponentCreateRequestTrigger/components/NewComponentsRequestDialog/models/SimpleComponent';
import { ComponentCreateRequestRequestPriority } from '@gql/graphql';
import { type StringOption } from '@models/Option';
import { flatMapEdges } from '@utils/flatMapEdges';
import { capitalizeString } from '@utils/stringFormatUtils';

export interface UseNewComponentRequestOptions {
    vendors: StringOption[];
    priorities: StringOption[];
    componentNames: string[];
    componentVersions: string[];
    fetchVendors: (name: string) => void;
    fetchComponents: (name: string, vendorId?: string | StringOption) => void;
}

export function useNewComponentRequestOptions(): UseNewComponentRequestOptions {
    const [fetchVendorsFn] = useLazyQuery(GET_ALL_COMPONENT_VENDORS_BY_NAME);
    const [fetchComponentsFn] = useLazyQuery(GET_COMPONENTS_BY_VENDOR);

    const [vendors, setVendors] = useState<StringOption[]>([]);
    const [components, setComponents] = useState<SimpleComponent[]>([]);

    const componentNames = useMemo(
        () => Array.from(new Set(components.map((c) => c.name))),
        [components],
    );
    const componentVersions = useMemo(
        () => Array.from(new Set(components.map((c) => c.version))),
        [components],
    );

    const priorityOrder = [
        ComponentCreateRequestRequestPriority.Low,
        ComponentCreateRequestRequestPriority.Medium,
        ComponentCreateRequestRequestPriority.High,
    ];
    const priorities: StringOption[] = Object.values(
        ComponentCreateRequestRequestPriority,
    )
        .sort((a, b) => priorityOrder.indexOf(a) - priorityOrder.indexOf(b))

        .map((priority) => ({
            label: capitalizeString(priority),
            value: priority,
        }));

    async function fetchVendors(name: string): Promise<void> {
        const { data } = await fetchVendorsFn({
            variables: {
                partialName: name,
            },
        });

        const newOptions: StringOption[] =
            data?.componentVendors.edges.map(
                (edge: { node: { id: string; name: string } }) => ({
                    value: edge.node.id,
                    label: edge.node.name,
                }),
            ) ?? [];

        setVendors(newOptions);
    }

    async function fetchComponents(
        partialName: string,
        componentVendor?: string | StringOption,
    ): Promise<void> {
        const { data } = await fetchComponentsFn({
            variables: {
                partialName,
                vendorId:
                    componentVendor && typeof componentVendor !== 'string'
                        ? componentVendor.value
                        : null,
            },
        });

        const nodes = flatMapEdges(data?.components);
        setComponents(nodes);
    }

    return {
        vendors,
        fetchVendors,
        priorities,
        componentNames,
        componentVersions,
        fetchComponents,
    };
}
